import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { notificationsAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const NotificationsContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) return { notifications: [], unreadCount: 0, refresh: () => {}, markAllRead: () => {}, markRead: () => {} };
  return context;
};

export const NotificationsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) { setNotifications([]); setUnreadCount(0); return; }
    try {
      const res = await notificationsAPI.getAll();
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* silent */ }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
    if (!isAuthenticated) return;
    const interval = setInterval(refresh, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [refresh, isAuthenticated]);

  const markAllRead = useCallback(async () => {
    try {
      await notificationsAPI.readAll();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  }, []);

  const markRead = useCallback(async (id) => {
    try {
      await notificationsAPI.read(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  }, []);

  return (
    <NotificationsContext.Provider value={{ notifications, unreadCount, refresh, markAllRead, markRead }}>
      {children}
    </NotificationsContext.Provider>
  );
};
